/* Architecture reader: a viewport-level gesture surface with one file action. */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PinchZoom, PinchZoomHandle } from './PinchZoom';
import { getWebApp, haptic, useBackInterceptor } from '../telegram';

interface Props {
  open: boolean;
  src: string | null;
  title: string;
  onClose: () => void;
  onDownloadPdf: () => Promise<void>;
  targetPoint?: { x: number; y: number } | null;
  targetFromOlderVersion?: boolean;
}

export function PosterViewer({ open, src, title, onClose, onDownloadPdf, targetPoint, targetFromOlderVersion }: Props) {
  const zoom = useRef<PinchZoomHandle>(null);
  const anchor = useRef<HTMLSpanElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [portalHost, setPortalHost] = useState<Element | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const downloadActive = useRef(false);
  useBackInterceptor(open, onClose);

  // An animated tab is a containing block for position:fixed. Mount outside
  // that tab so the reader fills the Telegram viewport at every scroll offset.
  useLayoutEffect(() => {
    setPortalHost(anchor.current?.closest('.tma-app') ?? document.body);
  }, []);

  useEffect(() => {
    if (!open || !portalHost) return undefined;
    setNote(null);
    const app = getWebApp();
    try { app?.disableVerticalSwipes?.(); } catch { /* older client */ }
    const prev = document.body.style.overflow;
    const previousFocus = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        const buttons = closeButton.current?.closest('.tma-viewer')?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
        if (buttons?.length) {
          e.preventDefault();
          const index = Array.from(buttons).findIndex(button => button === document.activeElement);
          const next = e.shiftKey ? (index <= 0 ? buttons.length - 1 : index - 1) : (index + 1) % buttons.length;
          buttons[next].focus();
        }
      }
      if (e.key === 'Escape') onClose();
      if (e.key === '+' || e.key === '=') zoom.current?.zoomBy(1.25);
      if (e.key === '-') zoom.current?.zoomBy(1 / 1.25);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      previousFocus?.focus({ preventScroll: true });
      try { app?.enableVerticalSwipes?.(); } catch { /* older client */ }
    };
  }, [open, onClose, portalHost]);

  const download = useCallback(async () => {
    if (downloadActive.current) return;
    downloadActive.current = true;
    haptic.impact('light');
    setBusy(true);
    setNote(null);
    try {
      await onDownloadPdf();
    } catch (err: unknown) {
      haptic.notify('error');
      setNote((err as Error).message || 'Could not prepare the file');
    } finally {
      downloadActive.current = false;
      setBusy(false);
    }
  }, [onDownloadPdf]);

  return <>
    <span ref={anchor} hidden />
    {open && portalHost ? createPortal(
      <div className="tma-viewer" role="dialog" aria-modal="true" aria-label={title}>
        <div className="tma-viewer-head">
          <div className="tma-viewer-title" title={title}>{title}</div>
          <button ref={closeButton} type="button" className="tma-viewer-close" aria-label="Close architecture" onClick={onClose}>✕</button>
        </div>
        <div className="tma-viewer-stage">
          {src ? (
            <PinchZoom ref={zoom} src={src} resetKey={1} targetPoint={targetPoint} className="tma-viewer-zoom" />
          ) : (
            <div className="tma-viewer-empty" role="status" aria-label="Loading architecture"><span className="tma-spin tma-poster-spin" /></div>
          )}
        </div>
        {targetPoint ? <div className="tma-viewer-anchor-note" role="status">
          {targetFromOlderVersion ? 'Pinned on an earlier architecture version · check this location' : 'Pinned comment location'}
        </div> : null}
        <div className="tma-viewer-bar">
          {note ? <div className="tma-viewer-note" role="status">{note}</div> : null}
          <button type="button" className="tma-viewer-act" disabled={busy} onClick={() => void download()}>
            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12m-4-4 4 4 4-4M5 15v5h14v-5" /></svg>
            {busy ? 'Preparing PDF…' : 'Download PDF'}
          </button>
        </div>
      </div>, portalHost) : null}
  </>;
}
